import { ResourceCounts } from '../types/multiplayer';

/**
 * Represents resource storage capacity limits for a cell
 */
export interface ResourceCapacity {
  /** Maximum lipids that can be stored */
  maxLipids: number;
  
  /** Maximum glucose that can be stored */
  maxGlucose: number;
  
  /** Maximum ATP that can be stored */
  maxATP: number;
}

/**
 * Base resource capacity for stage 1 membrane
 */
const BASE_CAPACITY: ResourceCapacity = {
  maxLipids: 100,
  maxGlucose: 50,
  maxATP: 100
};

/**
 * Map of membrane stage to capacity multiplier
 * Each stage increases capacity proportionally to the size increase
 */
export const MEMBRANE_CAPACITY_MULTIPLIERS: { [stage: number]: number } = {
  1: 1.0,  // Base capacity
  2: 1.2,  // 20% more than base
  3: 1.5,  // 50% more than base
  4: 2.0   // Double the base capacity
};

/**
 * Gets the maximum resource capacity for a given membrane stage
 * @param stage Membrane expansion stage (1-4)
 * @returns ResourceCapacity with maximum values for each resource type
 */
export function getResourceCapacityForStage(stage: number): ResourceCapacity {
  const multiplier = MEMBRANE_CAPACITY_MULTIPLIERS[stage] || MEMBRANE_CAPACITY_MULTIPLIERS[1];
  
  return {
    maxLipids: Math.round(BASE_CAPACITY.maxLipids * multiplier),
    maxGlucose: Math.round(BASE_CAPACITY.maxGlucose * multiplier),
    maxATP: Math.round(BASE_CAPACITY.maxATP * multiplier)
  };
}

/**
 * Checks if adding the specified resources would exceed capacity
 * @param current Current resource counts
 * @param toAdd Resources to add
 * @param stage Current membrane stage (1-4)
 * @returns Object with excess amounts (0 if within capacity)
 */
export function calculateExcessResources(
  current: ResourceCounts,
  toAdd: Partial<ResourceCounts>,
  stage: number
): Partial<ResourceCounts> {
  const capacity = getResourceCapacityForStage(stage);
  const result: Partial<ResourceCounts> = {};
  
  // Calculate excess for each resource type
  if (toAdd.lipids) {
    const newTotal = (current.lipids || 0) + toAdd.lipids;
    result.lipids = Math.max(0, newTotal - capacity.maxLipids);
  }
  
  if (toAdd.glucose) {
    const newTotal = (current.glucose || 0) + toAdd.glucose;
    result.glucose = Math.max(0, newTotal - capacity.maxGlucose);
  }
  
  if (toAdd.atp) {
    const newTotal = (current.atp || 0) + (toAdd.atp || 0);
    result.atp = Math.max(0, newTotal - capacity.maxATP);
  }
  
  return result;
}

/**
 * Get the capped resources that can be added without exceeding capacity
 * @param current Current resource counts
 * @param toAdd Resources to add
 * @param stage Current membrane stage (1-4)
 * @returns Resources that can be safely added without exceeding capacity
 */
export function capResourcesToCapacity(
  current: ResourceCounts,
  toAdd: Partial<ResourceCounts>,
  stage: number
): Partial<ResourceCounts> {
  const capacity = getResourceCapacityForStage(stage);
  const result: Partial<ResourceCounts> = {};
  
  // Calculate capped amount for each resource type
  if (toAdd.lipids !== undefined) {
    const spaceAvailable = Math.max(0, capacity.maxLipids - current.lipids);
    result.lipids = Math.min(toAdd.lipids, spaceAvailable);
  }
  
  if (toAdd.glucose !== undefined) {
    const spaceAvailable = Math.max(0, capacity.maxGlucose - current.glucose);
    result.glucose = Math.min(toAdd.glucose, spaceAvailable);
  }
  
  if (toAdd.atp !== undefined) {
    const spaceAvailable = Math.max(0, capacity.maxATP - (current.atp || 0));
    result.atp = Math.min(toAdd.atp || 0, spaceAvailable);
  }
  
  return result;
}

/**
 * Checks if resources are at or near capacity
 * @param resources Current resource counts
 * @param stage Current membrane stage (1-4)
 * @returns Object with fullness status for each resource type
 */
export function getResourceFullnessStatus(
  resources: ResourceCounts,
  stage: number
): {[key: string]: {current: number, max: number, percentage: number}} {
  const capacity = getResourceCapacityForStage(stage);
  
  return {
    lipids: {
      current: resources.lipids,
      max: capacity.maxLipids,
      percentage: Math.min(100, Math.round((resources.lipids / capacity.maxLipids) * 100))
    },
    glucose: {
      current: resources.glucose,
      max: capacity.maxGlucose,
      percentage: Math.min(100, Math.round((resources.glucose / capacity.maxGlucose) * 100))
    },
    atp: {
      current: resources.atp || 0,
      max: capacity.maxATP,
      percentage: Math.min(100, Math.round(((resources.atp || 0) / capacity.maxATP) * 100))
    }
  };
} 